import { useEffect, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Bell } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { ThemeToggle } from "@/components/common/ThemeToggle";
import { UserMenu } from "@/components/common/UserMenu";
import { PWAInstallButton } from "@/components/common/PWAInstallButton";
import lumoLogo from "@/assets/lumo-logo.svg";
import lumoLogoDark from "@/assets/lumo-logo-dark.svg";
import { useTheme } from "next-themes";

interface UserProfile {
  first_name?: string;
  last_name?: string;
  avatar_url?: string;
}

export function UserProfileHeader() {
  const { user } = useAuth();
  const { resolvedTheme } = useTheme();
  const [profile, setProfile] = useState<UserProfile | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user?.id) return;
      
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('first_name, last_name, avatar_url')
          .eq('id', user.id)
          .single();
        
        if (error && error.code !== 'PGRST116') {
          console.error('Error fetching profile:', error);
          return;
        }
        
        setProfile(data);
      } catch (error) {
        console.error('Error fetching profile:', error);
      }
    };

    fetchProfile();
  }, [user?.id]);

  const getInitials = () => {
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name[0]}${profile.last_name[0]}`;
    }
    return user?.email?.[0]?.toUpperCase() || 'U';
  };

  const getGreeting = () => {
    const firstName = profile?.first_name || 'Usuário';
    return `Olá, ${firstName}`;
  };

  return (
    <div className="bg-background border-b border-border px-6 py-4 fixed top-0 left-0 right-0 z-50">
      <div className="flex items-center justify-between max-w-full">
        <div className="flex items-center gap-3">
          <img 
            src={resolvedTheme === 'dark' ? lumoLogoDark : lumoLogo}
            alt="Lumo" 
            className="h-12 w-12"
          />
          <div>
             <h1 className="text-base font-bold text-foreground">{getGreeting()}</h1>
             <p className="text-sm text-muted-foreground">Bem vindo ao Lumo</p>
           </div>
        </div>
        <div className="flex items-center gap-2">
          <PWAInstallButton variant="compact" />
          <ThemeToggle />
          <div className="bg-accent rounded-full p-2">
            <Bell className="h-5 w-5 text-muted-foreground" />
          </div>
          <UserMenu />
        </div>
      </div>
    </div>
  );
}